import {
  DynamicDrawUsage,
  BatchedMesh,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Vector3,
  type BufferGeometry,
  type Material,
} from 'three';
import { layoutToWorld } from '../../core/coords';
import type { Animated, BuildContext, FeatureOutput, NightLight, WorldFeature } from '../types';
import { edgeIdFor } from '../mesh/attributes';
import { buildHorseModel, type LifeModel } from './models';
import { createContactShadows } from '../../render/contactShadows';
import { quadrupedLegMatrix } from './quadrupedGait';
import { buildDogModel } from './dog';
import { buildLifeDoors } from './doors';
import { lifeDoorways } from './crowd/crowdDoorways';
import { buildLantern, LANTERN_FLAME, LANTERN_RING } from './props';
import { createCastCitizens, type CastCitizens } from './castCitizens';
import { planNpcLife } from './routinePlan';
import { WATCH } from './nightRoutines';
import { BURGLARS } from './nightBurglars';
import { createLifeClearance, lifeObstacleRings, openSquares, stepClear } from './routes';
import { createCrowdMotion } from './crowdMotion';
import { batchBodies } from './bodyBatch';
import {
  CITIZEN_ROLES,
  type LifeEntrance,
  type LifePose,
  type LifeRoute,
  type LitWindow,
} from './types';
import { FLAME_COLOUR, PANE_COLOUR } from '../../render/night/nightLights';
import { townspersonName } from '../../communication/townspersonName';
import type { Townsperson } from '../../communication/types';

type PartKind = 'body' | 'forelegs' | 'hindlegs' | 'lantern';
interface CastPart {
  readonly kind: PartKind;
  readonly routes: readonly LifeRoute[];
  readonly mesh: InstancedMesh | BatchedMesh;
}

function mesh(
  geometry: BufferGeometry,
  material: Material,
  routes: readonly LifeRoute[],
  limbs: number,
  name: string
): InstancedMesh {
  const ids = routes.flatMap((route) =>
    Array.from({ length: limbs }, () =>
      edgeIdFor(
        1700 +
          Number(route.id.split('-').at(-1)) +
          CITIZEN_ROLES.indexOf(route.role as (typeof CITIZEN_ROLES)[number]) * 5
      )
    )
  );
  geometry.setAttribute('aInstanceEdgeId', new InstancedBufferAttribute(Float32Array.from(ids), 1));
  geometry.setAttribute(
    'aPainterScale',
    new InstancedBufferAttribute(Float32Array.from(ids.flatMap(() => [1, 1])), 2)
  );
  const result = new InstancedMesh(geometry, material, routes.length * limbs);
  result.name = name;
  result.frustumCulled = false;
  result.instanceMatrix.setUsage(DynamicDrawUsage);
  return result;
}

/* A candle behind horn: how far its light carries over the street, and the glow at the lantern
   itself, in metres. Estimates set by eye against the reference's "deep darkness". */
const LANTERN = { reach: 11, glow: 0.9 } as const;
/* A candle seen through leaded glass: the glow over the glazed lights, a little past their edges.
   It throws no light on the street: with a reach, the window's own reveal shone like a hearth. */
const PANE = { reach: 0, spread: 1.25 } as const;

export function windowLights(windows: readonly LitWindow[]): NightLight[] {
  return windows.map((window, i) => ({
    position: window.position,
    colour: PANE_COLOUR,
    reach: PANE.reach,
    size: window.height * PANE.spread,
    aspect: window.width / window.height,
    outward: window.outward,
    phase: i * 2.399,
    from: window.from,
  }));
}

const animal = (route: LifeRoute): boolean => route.role === 'horse' || route.role === 'dog';

function trianglesOf(object: InstancedMesh | BatchedMesh): number {
  const { index, attributes } = object.geometry;
  return (index ? index.count : attributes['position']!.count) / 3;
}

/**
 * The cast: the people with a routine of their own, drawn as the citizens made for the city
 * (castCitizens.ts), their horses and dogs, the lanterns of those out after dark, and the doors
 * they go in and out by. Without the citizens' file there are no people to draw, and the animals
 * and the doors are all there is.
 */
export function buildLifeCast(
  ctx: BuildContext,
  routes: readonly LifeRoute[],
  entrances: readonly LifeEntrance[] = [],
  windows: readonly LitWindow[] = []
): FeatureOutput {
  const material = ctx.materials.painted({ instanced: true });
  const models = new Map<LifeRoute, LifeModel>();
  const parts: CastPart[] = [];
  const used = new Set<BufferGeometry>();
  const add = (
    kind: PartKind,
    selected: readonly LifeRoute[],
    geometry: BufferGeometry,
    name: string
  ): void => {
    used.add(geometry);
    parts.push({
      kind,
      routes: selected,
      mesh: mesh(geometry, material, selected, kind === 'body' || kind === 'lantern' ? 1 : 2, name),
    });
  };
  for (const role of ['horse', 'dog'] as const) {
    const selected = routes.filter((route) => route.role === role);
    if (!selected.length) continue;
    const model = role === 'horse' ? buildHorseModel(ctx.palette) : buildDogModel();
    selected.forEach((route) => models.set(route, model));
    add('body', selected, model.body, `life-${role}-body`);
    add('forelegs', selected, model.arm, `life-${role}-forelegs`);
    add('hindlegs', selected, model.leg, `life-${role}-hindlegs`);
  }
  if (parts.some((part) => part.kind === 'body')) {
    const bodies = parts.filter((part) => part.kind === 'body');
    const batch = batchBodies(
      bodies.map((part) => ({ geometry: part.mesh.geometry, routes: part.routes })),
      ctx.materials.painted()
    );
    for (const body of bodies) {
      parts.splice(parts.indexOf(body), 1);
      used.delete(body.mesh.geometry);
      body.mesh.dispose();
    }
    parts.unshift({ kind: 'body', ...batch });
  }
  // Discard duplicate templates now; the meshes own every geometry retained for world disposal.
  for (const model of new Set(models.values()))
    for (const geometry of [model.body, model.arm, model.leg])
      if (!used.has(geometry)) geometry.dispose();

  const shadows = createContactShadows(routes.filter(animal).length, ctx.materials.painted());
  const animalBody = parts.find((part) => part.kind === 'body')?.mesh;
  if (animalBody) {
    animalBody.add(shadows.root);
    animalBody.addEventListener('dispose', () => {
      shadows.dispose();
    });
  } else shadows.dispose();

  const people = ctx.citizens ? routes.filter((route) => !animal(route)) : [];
  let citizens: CastCitizens | undefined;
  if (ctx.citizens && people.length) {
    ctx.materials.setCitizenPoses(ctx.citizens.poses, ctx.citizens.bones.length);
    citizens = createCastCitizens(ctx.materials.painted(), ctx.citizens, people);
    citizens.mesh.userData['lifeRoutes'] = people;
  }
  const carriers = people.filter((route) => route.routine?.some((step) => step.prop === 'lantern'));
  if (carriers.length) add('lantern', carriers, buildLantern(), 'life-lantern');
  const memberOf = new Map(people.map((route, i) => [route, i]));
  const names = people.map((route, i) => townspersonName(route.id, citizens!.figures[i]!.sex));
  let townspeople: Townsperson[] = [];

  const base = new Matrix4(),
    local = new Matrix4(),
    scale = new Vector3(),
    hand = new Vector3();
  let daytime = true;
  /* The day's cast goes in at dusk and the night's comes out; neither yields to the other. */
  let onWatch = false,
    deadOfNight = false;
  const out = (route: LifeRoute): boolean =>
    (!route.daytime || daytime) &&
    (!route.night || !daytime) &&
    (!route.watch || onWatch) &&
    (!route.late || deadOfNight);
  const flame = new Vector3();
  let lanterns: NightLight[] = [];
  const clearance = createLifeClearance(
    ctx.site,
    ctx.lifePlan?.clearanceRings ?? lifeObstacleRings(ctx.layout, ctx.lots, ctx.site),
    openSquares(ctx.layout)
  );
  const motion = createCrowdMotion(
    routes,
    (a, b, radius) => stepClear({ a, b, radius }, clearance),
    out
  );
  const doorById = new Map(entrances.map((entry) => [entry.id, entry]));
  const doorways = lifeDoorways(entrances, ctx.citizens ? ctx.lifePlan?.crowd : undefined);
  const doors = doorways.length ? buildLifeDoors(ctx, doorways) : null;

  /* Where one of the cast stands, into `base`: on the ground, or on the step of the door it is
     passing; drawn at no size while it is not out. */
  function stand(route: LifeRoute, pose: LifePose, visible: boolean): void {
    let ground = ctx.site.queries.groundHeightAt(pose.position);
    const entry = pose.door ? doorById.get(pose.door) : undefined;
    if (entry) {
      const dx = pose.position[0] - entry.position[0],
        dy = pose.position[1] - entry.position[1];
      const d = dx * entry.outward[0] + dy * entry.outward[1];
      const support =
        d < 0 ? entry.groundY : d < entry.stepReach ? entry.groundY + entry.stepHeight : ground;
      ground = Math.max(ground, support);
    }
    const [x, y, z] = layoutToWorld(pose.position, ground);
    base
      .makeRotationY(pose.heading)
      .scale(scale.setScalar(visible ? route.scale : 0))
      .setPosition(x, y, z);
  }

  const animated: Animated = {
    id: 'street-life',
    townspeople: {
      list: () => townspeople,
      converse(id, visitor) {
        const route = id === null ? null : people.find((person) => person.id === id);
        if (route === undefined || (id !== null && !townspeople.some((p) => p.id === id)))
          return false;
        motion.hold(route, [visitor[0], -visitor[2]]);
        return true;
      },
    },
    setTimeOfDay(minutes, activity) {
      daytime = activity > 0.05;
      onWatch = minutes >= WATCH.musterMinutes || minutes < WATCH.standDownMinutes;
      deadOfNight = minutes >= BURGLARS.fromMinutes || minutes < BURGLARS.untilMinutes;
    },
    update(timeSeconds) {
      const poses = motion.update(timeSeconds);
      if (doors)
        doors.update(
          timeSeconds,
          [...poses].flatMap(([route, pose]) => (out(route) ? [pose] : []))
        );
      shadows.begin();
      lanterns = [];
      townspeople = [];
      if (citizens) {
        const visibility: boolean[] = [],
          walking: boolean[] = [];
        citizens.begin();
        for (const route of people) {
          const pose = poses.get(route)!;
          const visible = pose.visible !== false && out(route);
          visibility.push(visible);
          walking.push(visible && pose.walking);
          stand(route, pose, visible);
          if (visible) {
            const i = memberOf.get(route)!;
            townspeople.push({
              id: route.id,
              name: names[i]!,
              trade:
                route.role === 'carer'
                  ? route.streetId === 'landmark-leprozenhuis'
                    ? 'leprozen_carer'
                    : 'carer'
                  : route.role === 'physician'
                    ? 'physician'
                    : citizens.figures[i]!.trade,
              position: [base.elements[12], base.elements[13], base.elements[14]],
              height: route.scale * 1.8,
            });
          }
          citizens.draw(base, {
            walked: motion.distance(route),
            scale: route.scale,
            gait: pose.gaitWeight,
            working: pose.action === 'work',
            seconds: timeSeconds,
          });
        }
        citizens.end();
        citizens.mesh.userData['lifeVisibility'] = visibility;
        citizens.mesh.userData['lifeWalking'] = walking;
      }
      for (const part of parts) {
        const visibility: boolean[] = [];
        const walking: boolean[] = [];
        part.routes.forEach((route, i) => {
          const pose = poses.get(route)!;
          const visible =
            pose.visible !== false &&
            out(route) &&
            (part.kind !== 'lantern' || pose.prop === 'lantern');
          visibility.push(visible);
          walking.push(visible && pose.walking);
          stand(route, pose, visible);
          if (part.kind === 'lantern') {
            // It hangs by its ring from the hand that carries it, and swings as the hand does.
            citizens!.hand(memberOf.get(route)!, hand);
            local.makeTranslation(
              hand.x - LANTERN_RING[0],
              hand.y - LANTERN_RING[1],
              hand.z - LANTERN_RING[2]
            );
            part.mesh.setMatrixAt(i, local.premultiply(base));
            if (visible) {
              flame.set(...LANTERN_FLAME).applyMatrix4(local);
              lanterns.push({
                position: [flame.x, flame.y, flame.z],
                colour: FLAME_COLOUR,
                reach: LANTERN.reach,
                size: LANTERN.glow,
                phase: i * 2.399,
              });
            }
            return;
          }
          if (part.kind === 'body') {
            if (part.mesh instanceof BatchedMesh) part.mesh.setVisibleAt(i, visible);
            part.mesh.setMatrixAt(i, base);
            if (visible)
              shadows.add(
                base,
                route.role === 'horse' ? 1.15 : 0.5,
                route.role === 'horse' ? 0.45 : 0.22
              );
            return;
          }
          const model = models.get(route)!;
          const fore = part.kind === 'forelegs';
          for (const side of [-1, 1] as const) {
            quadrupedLegMatrix(
              local,
              model,
              motion.distance(route) / route.scale,
              pose.gaitWeight,
              fore,
              side
            );
            part.mesh.setMatrixAt(i * 2 + (side + 1) / 2, local.premultiply(base));
          }
        });
        part.mesh.userData['lifeVisibility'] = visibility;
        part.mesh.userData['lifeWalking'] = walking;
        if (part.mesh instanceof InstancedMesh) part.mesh.instanceMatrix.needsUpdate = true;
      }
      shadows.end();
    },
    lights: { moving: true, list: () => lanterns },
  };
  animated.update(0);
  const cast = [...(citizens ? [citizens.mesh] : []), ...parts.map((part) => part.mesh)];
  const objects = [...cast, ...(doors?.objects ?? [])];
  for (const part of parts) part.mesh.userData['lifeRoutes'] = part.routes;
  return {
    objects,
    animated: [
      ...(cast.length ? [animated] : []),
      ...(windows.length
        ? [
            {
              id: 'household-lights',
              update: () => undefined,
              lights: { moving: false, list: () => windowLights(windows) },
            },
          ]
        : []),
    ],
    obstacles: [],
    counts: {
      citizens: routes.filter((r) => !animal(r)).length,
      horses: routes.filter((r) => r.role === 'horse').length,
      dogs: routes.filter((r) => r.role === 'dog').length,
      lifeRoutines: routes.filter((r) => r.routine).length,
      lifeDoors: entrances.length,
      lifeMeshes: objects.length,
      lifeTemplateTriangles: objects.reduce(
        (sum, object) => sum + trianglesOf(object),
        animalBody ? 2 : 0
      ),
      ...Object.fromEntries(
        CITIZEN_ROLES.map((role) => [`life-${role}`, routes.filter((r) => r.role === role).length])
      ),
    },
  };
}

export const lifeFeature: WorldFeature = {
  id: 'life',
  label: 'Street life',
  weight: 1,
  build(ctx) {
    const plan =
      ctx.lifePlan ??
      planNpcLife(
        { layout: ctx.layout, palette: ctx.palette, painterView: ctx.painterView },
        ctx.site,
        ctx.lots,
        1538
      );
    return buildLifeCast(ctx, plan.routes, plan.entrances, plan.windows);
  },
};
